import { Pipe, PipeTransform } from '@angular/core';

/**
 * Formats a number to a fixed number of decimals by TRUNCATING, never rounding.
 *
 * Angular's number pipe and toFixed both round, which is wrong for a field size
 * shown next to an input validated against it. A field of 4.226 ha renders as
 * "4.23", the user types 4.23 as the cultivar size, and the save is rejected
 * because 4.23 exceeds 4.226 — the screen having just told them the field is
 * 4.23. Truncating guarantees the displayed value is never larger than the real
 * one, so entering exactly what is on screen always validates.
 *
 * Deliberately not Math.floor(value * 100) / 100. That looks equivalent and is
 * not: 4.23 * 100 is 422.99999999999994 in binary floating point, so flooring it
 * yields 4.22 and an exact 4.23 would display as 4.22. This works on the decimal
 * string instead, where 4.23 stays 4.23 and only genuinely-longer values lose
 * digits.
 *
 * Exported as a function as well as a pipe because the same rule has to apply to
 * validation messages built in TypeScript. An error reading "4.23 exceeds field
 * size of 4.23" is the bug this exists to prevent.
 */
export function truncateDecimals(value: number | string | null | undefined, digits: number = 2): string {
    if (value === null || value === undefined || value === '') return '';

    const parsed = Number(value);
    if (!isFinite(parsed)) return '';

    const decimals = Math.max(0, Math.trunc(digits));
    const negative = parsed < 0;
    let text = Math.abs(parsed).toString();

    // toString switches to exponential notation outside roughly 1e-7 to 1e21.
    // Field sizes never reach that, but a fixed-notation fallback costs little and
    // stops the string surgery below producing nonsense if one ever does.
    if (text.indexOf('e') !== -1 || text.indexOf('E') !== -1) {
        text = Math.abs(parsed).toFixed(Math.min(20, decimals + 10));
    }

    const point = text.indexOf('.');
    let whole: string;
    let fraction: string;

    if (point === -1) {
        whole = text;
        fraction = '';
    } else {
        whole = text.slice(0, point);
        // slice, not round: everything past the requested precision is dropped.
        fraction = text.slice(point + 1, point + 1 + decimals);
    }

    if (decimals > 0) {
        fraction = fraction.padEnd(decimals, '0');
    }

    const formatted = decimals > 0 ? `${whole}.${fraction}` : whole;
    return negative && Number(formatted) !== 0 ? `-${formatted}` : formatted;
}

@Pipe({
    name: 'truncDecimals',
    standalone: true
})
export class TruncateDecimalsPipe implements PipeTransform {
    transform(value: number | string | null | undefined, digits: number = 2): string {
        return truncateDecimals(value, digits);
    }
}
